import { z } from 'zod'

import { lenientList, moneySchema, percentSchema } from '@/schemas/common'
import { isoDateSchema } from '@/schemas/portfolio'

/**
 * Stock › Floorsheet's own read — `/api/v5/company/{symbol}/floorsheet/` — as
 * the app holds it once `profileService` has mapped it.
 *
 * Its own file beside `company-profile.ts` (the other tabs' shapes), with the
 * same rules: null is "not reported", decimals become `Money` here, once.
 *
 * Only what mm-be guarantees is required (`services/company/floorsheet.py` on
 * dev). Every comparison is nullable by design: none for `week` (a week's total
 * against "a normal day" is not like-for-like), none for a stock with under 30
 * sessions of history, and the two averages none until the session settles.
 */

export const floorsheetTradeSummarySchema = z.object({
  trades: z.number().int(),
  kitta: z.number().int(),
  value: moneySchema,
  /** Kitta per trade, to the paisa; null with no trades. */
  averageTradeSize: moneySchema.nullable(),
  /** The biggest single print, in kitta. */
  largestTradeKitta: z.number().int().nullable(),
  /** Signed, against the mean of the 30 sessions before this one: "12.00" is +12%. */
  tradesVsAveragePercent: percentSchema.nullable(),
  /** Kitta as a multiple of the same 30-session mean: "2.10" is 2.1 times. */
  kittaVsNormalDay: moneySchema.nullable(),
  /** Rank by value traded among every scrip that traded that session, 1 is the busiest. */
  turnoverRank: z.number().int().nullable(),
  rankedScrips: z.number().int().nullable(),
})

/**
 * One house's buying and selling of this stock over the window. Every house
 * that traded it has both sides here, so a side it did not trade is a true
 * nought, never "not known".
 */
export const floorsheetBrokerRowSchema = z.object({
  code: z.string().min(1),
  /** mm-be's name, or "Broker 07" when it has none (`brokerDisplayName`). */
  name: z.string().min(1),
  /** The review average out of five, one decimal; null when nobody has rated it. */
  rating: moneySchema.nullable(),
  bought: z.number().int(),
  sold: z.number().int(),
  /** Bought less sold, mm-be's own subtraction. */
  net: z.number().int(),
})

export const companyFloorsheetSummarySchema = z.object({
  /** The window's first and last session; null, with `summary`, when the floorsheet holds none. */
  fromDate: isoDateSchema.nullable(),
  toDate: isoDateSchema.nullable(),
  /** Null is "no floorsheet for this window", not "nobody traded" — that is zeros. */
  summary: floorsheetTradeSummarySchema.nullable(),
  /** Busiest first (bought plus sold), cut to the count asked for. */
  brokers: lenientList(floorsheetBrokerRowSchema, 'floorsheet broker'),
  /** Every house that traded it in the window, before the cut. */
  brokersTotal: z.number().int(),
})

export type FloorsheetTradeSummary = z.infer<typeof floorsheetTradeSummarySchema>
export type FloorsheetBrokerRow = z.infer<typeof floorsheetBrokerRowSchema>
export type CompanyFloorsheetSummary = z.infer<typeof companyFloorsheetSummarySchema>
